import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { useAuthStore } from './auth.store';
import type { StoredCV } from '../hooks/useCV';

interface CVState {
  cvs: StoredCV[];
  loading: boolean;
  cvError: Error | null;
  fetchCVs: () => Promise<void>;
  setCVs: (cvs: StoredCV[]) => void;
  setLoading: (loading: boolean) => void;
  setError: (error: Error | null) => void;
}

const useCVStore = create<CVState>((set, get) => ({
  cvs: [],
  loading: true,
  cvError: null,
  setCVs: (cvs) => set({ cvs }),
  setLoading: (loading) => set({ loading }),
  setError: (error) => set({ cvError: error }),
  fetchCVs: async () => {
    const { user } = useAuthStore.getState();
    if (!user) {
      set({ loading: false, cvs: [] });
      return;
    }
    set({ loading: true, cvError: null });

    // Assuming subscription logic is handled elsewhere or we can get it
    // For simplicity, I'll replicate the logic from the original hook
    // A better approach would be to access subscription store if possible
    const { data: subscription } = await supabase
      .from('user_analyses')
      .select('plan')
      .eq('user_id', user.id)
      .single();

    const isFreeTier = !subscription || subscription.plan === 'free';
    const limit = isFreeTier ? 1 : 3;

    try {
      const { data, error } = await supabase
        .from('cvs')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        throw error;
      }
      set({ cvs: data || [], loading: false });
    } catch (error: any) {
      // console.error('Error fetching CVs:', error);
      set({ cvError: error, cvs: [], loading: false });
    }
  },
}));

useAuthStore.subscribe(
  (state, prevState) => {
    const newUser = state.user;
    const oldUser = prevState.user;

    // Fetch profile if the user logs in or changes
    if (newUser?.id && newUser.id !== oldUser?.id) {
      useCVStore.getState().fetchCVs();
    }
    // Clear profile if the user logs out
    else if (!newUser && oldUser) {
      useCVStore.setState({ cvs: [], loading: false, cvError: null });
    }
  }
);


export { useCVStore };